import {
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import { type AppError, clinicMessageKeys, request, withPage } from '@/shared/api';

import type { MyMessages, SentMessages } from '../model/types';
import { myMessagesSchema, sentMessageListSchema, toMyMessages, toSentMessage } from './schema';

/** `?before=<id>` — the oldest message already on screen; the next page is older. */
function pagePath(path: string, before: string | null): string {
  return before === null ? path : `${path}?${new URLSearchParams({ before }).toString()}`;
}

/** The cursor for the next page: the last (oldest) id of the last page, if there is more. */
function nextCursor(page: {
  readonly items: readonly { readonly id: string }[];
  readonly hasMore: boolean;
}): string | undefined {
  return page.hasMore ? page.items.at(-1)?.id : undefined;
}

/**
 * «پیام‌های ارسال‌شده» — `sms.broadcast`, like sending. One page at a time,
 * newest first; the previous page stays on screen while the next one loads.
 */
export function useSentMessages(
  page: number,
  perPage: number,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<SentMessages, AppError> {
  return useQuery({
    queryKey: clinicMessageKeys.sentPage(page, perPage),
    queryFn: async ({ signal }): Promise<SentMessages> => {
      const dto = await request(withPage('/staff/messages', page, perPage), {
        schema: sentMessageListSchema,
        signal,
      });

      return { items: dto.items.map(toSentMessage), ...dto.page };
    },
    placeholderData: (previous) => previous,
    // the read count moves as patients open their app
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

/**
 * The signed-in patient's messages and how many are unread. The tab bar asks
 * for the same key to draw its count, so the two can never disagree.
 *
 * Twenty at a time; the unread count is the server's total and always comes
 * from the newest page, so loading older ones never changes the badge.
 */
export function useMyMessages(
  options: { readonly enabled?: boolean; readonly refetchInterval?: number } = {},
): UseInfiniteQueryResult<MyMessages, AppError> {
  return useInfiniteQuery<MyMessages, AppError, MyMessages, readonly unknown[], string | null>({
    queryKey: clinicMessageKeys.mine(),
    initialPageParam: null,
    queryFn: async ({ signal, pageParam }): Promise<MyMessages> =>
      toMyMessages(
        await request(pagePath('/messages', pageParam), {
          schema: myMessagesSchema,
          signal,
          auth: 'account',
        }),
      ),
    getNextPageParam: nextCursor,
    select: flattenMyMessages,
    // «دکتر امروز دیرتر می‌رسند» is only useful the same hour it is sent
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: options.refetchInterval ?? false,
    enabled: options.enabled ?? true,
  });
}

function flattenMyMessages(data: InfiniteData<MyMessages, string | null>): MyMessages {
  return {
    items: data.pages.flatMap((page) => page.items),
    unreadCount: data.pages[0]?.unreadCount ?? 0,
    hasMore: data.pages.at(-1)?.hasMore ?? false,
  };
}
